(function() {
    'use strict';

    angular
        .module('wamasysApp')
        .controller('EventFieldDefinitionDialogController', EventFieldDefinitionDialogController);

    EventFieldDefinitionDialogController.$inject = ['$scope', '$uibModalInstance', 'entity', 'EventFieldDefinition', 'AlertService'];

    function EventFieldDefinitionDialogController($scope, $uibModalInstance, entity, EventFieldDefinition, AlertService) {
        var vm = this;

        vm.fieldDefinition = entity;
        vm.isEdit = !!vm.fieldDefinition.id;
        vm.dataTypeOptions = buildDataTypeOptions();
        vm.constraintDefinitions = buildConstraintDefinitions();
        vm.validationTypes = buildValidationTypes();

        vm.clear = clear;
        vm.save = save;
        vm.addValidationRule = addValidationRule;
        vm.removeValidationRule = removeValidationRule;
        vm.requiresSingleValue = requiresSingleValue;
        vm.requiresValuesList = requiresValuesList;
        vm.availableValidationTypes = availableValidationTypes;
        vm.validationTypeHelp = validationTypeHelp;
        vm.validationValuePlaceholder = validationValuePlaceholder;
        vm.onValidationTypeChange = onValidationTypeChange;
        vm.supportsConstraint = supportsConstraint;
        vm.addAdditionalConstraint = addAdditionalConstraint;
        vm.removeAdditionalConstraint = removeAdditionalConstraint;
        vm.hasVisibleConstraintDefinitions = hasVisibleConstraintDefinitions;
        vm.supportsPossibleValues = supportsPossibleValues;
        vm.onDataTypeChange = onDataTypeChange;

        vm.fieldDefinition.active = vm.fieldDefinition.active !== false;
        vm.fieldDefinition.requiredByDefault = !!vm.fieldDefinition.requiredByDefault;
        vm.fieldDefinition.hiddenForEncoder = !!vm.fieldDefinition.hiddenForEncoder;
        vm.fieldDefinition.constraintsJson = vm.fieldDefinition.constraintsJson || '{}';
        vm.fieldDefinition.exampleValuesJson = vm.fieldDefinition.exampleValuesJson || '[]';
        vm.fieldDefinition.valuePossibleJson = vm.fieldDefinition.valuePossibleJson || '[]';

        vm.validationRules = parseValidationRules(vm.fieldDefinition.validationsJson);
        vm.constraintsModel = parseConstraints(vm.fieldDefinition.constraintsJson);
        vm.valuePossibleText = joinLines(parseStringArray(vm.fieldDefinition.valuePossibleJson));
        vm.exampleValuesText = joinLines(parseStringArray(vm.fieldDefinition.exampleValuesJson));
        applyDataTypePreset(false);

        function clear() {
            $uibModalInstance.dismiss('cancel');
        }

        function save() {
            vm.isSaving = true;

            try {
                vm.fieldDefinition.validationsJson = JSON.stringify(serializeValidationRules(vm.validationRules), null, 2);
                syncPossibleValuesFromRules();
                vm.fieldDefinition.valuePossibleJson = supportsPossibleValues()
                    ? JSON.stringify(resolveValueOptionsForSave(), null, 2)
                    : '[]';
                vm.fieldDefinition.exampleValuesJson = JSON.stringify(splitMultilineValues(vm.exampleValuesText), null, 2);
                vm.fieldDefinition.constraintsJson = JSON.stringify(serializeConstraints(), null, 2);
            } catch (e) {
                AlertService.error('Le paramétrage du dictionnaire contient une valeur invalide.');
                vm.isSaving = false;
                return;
            }

            if (vm.fieldDefinition.id !== null) {
                EventFieldDefinition.update(vm.fieldDefinition, onSaveSuccess, onSaveError);
            } else {
                EventFieldDefinition.save(vm.fieldDefinition, onSaveSuccess, onSaveError);
            }
        }

        function onSaveSuccess(result) {
            $scope.$emit('wamasysApp:eventFieldDefinitionUpdate', result);
            $uibModalInstance.close(result);
            vm.isSaving = false;
        }

        function onSaveError(error) {
            vm.isSaving = false;
            AlertService.error(error.data && error.data.message ? error.data.message : 'Erreur lors de l\'enregistrement du champ.');
        }

        function addValidationRule() {
            vm.validationRules.push({
                type: defaultValidationTypeForCurrentField(),
                severity: 'ERROR',
                value: null,
                valuesText: ''
            });
        }

        function removeValidationRule(index) {
            vm.validationRules.splice(index, 1);
        }

        function requiresSingleValue(rule) {
            return getValidationType(rule.type).inputMode === 'single';
        }

        function requiresValuesList(rule) {
            return getValidationType(rule.type).inputMode === 'list';
        }

        function availableValidationTypes(rule) {
            return vm.validationTypes.filter(function(type) {
                return isCompatibleWithCurrentType(type) || (rule && rule.type === type.code);
            });
        }

        function validationTypeHelp(rule) {
            return getValidationType(rule.type).help;
        }

        function validationValuePlaceholder(rule) {
            return getValidationType(rule.type).placeholder || 'Valeur';
        }

        function onValidationTypeChange(rule) {
            if (!requiresSingleValue(rule)) {
                rule.value = null;
            }
            if (!requiresValuesList(rule)) {
                rule.valuesText = '';
            }
        }

        function supportsConstraint(constraint) {
            if (!constraint || !constraint.supportedDataTypes || !constraint.supportedDataTypes.length) {
                return true;
            }
            return constraint.supportedDataTypes.indexOf(effectiveDataType(vm.fieldDefinition.dataType)) !== -1;
        }

        function addAdditionalConstraint() {
            vm.constraintsModel.additional.push({key: '', value: ''});
        }

        function removeAdditionalConstraint(index) {
            vm.constraintsModel.additional.splice(index, 1);
        }

        function hasVisibleConstraintDefinitions() {
            var visible = false;
            angular.forEach(vm.constraintDefinitions, function(definition) {
                if (!visible && supportsConstraint(definition)) {
                    visible = true;
                }
            });
            return visible;
        }

        function buildConstraintDefinitions() {
            return [
                {key: 'min', label: 'Minimum', placeholder: '0', help: 'Borne technique minimale.', supportedDataTypes: ['integer', 'decimal'], valueType: 'number'},
                {key: 'max', label: 'Maximum', placeholder: '100000', help: 'Borne technique maximale.', supportedDataTypes: ['integer', 'decimal'], valueType: 'number'},
                {key: 'step', label: 'Pas conseillé', placeholder: '1 ou 0.01', help: 'Pas de saisie conseillé pour l\'interface.', supportedDataTypes: ['integer', 'decimal'], valueType: 'number'},
                {key: 'minDate', label: 'Date minimale', placeholder: '2026-01-01', help: 'Date minimale acceptée.', supportedDataTypes: ['date', 'datetime'], valueType: 'string'},
                {key: 'maxDate', label: 'Date maximale', placeholder: '2026-12-31', help: 'Date maximale acceptée.', supportedDataTypes: ['date', 'datetime'], valueType: 'string'},
                {key: 'maxSelections', label: 'Nombre maximal de choix', placeholder: '5', help: 'Limite le nombre de valeurs sélectionnables.', supportedDataTypes: ['list'], valueType: 'integer'}
            ];
        }

        function buildValidationTypes() {
            return [
                {code: 'MIN_VALUE', label: 'Valeur minimale', group: 'Numérique', inputMode: 'single', supportedDataTypes: ['integer', 'decimal'], placeholder: '0', help: 'La valeur doit être supérieure ou égale à ce seuil.'},
                {code: 'MAX_VALUE', label: 'Valeur maximale', group: 'Numérique', inputMode: 'single', supportedDataTypes: ['integer', 'decimal'], placeholder: '1000', help: 'La valeur doit être inférieure ou égale à ce seuil.'},
                {code: 'DECIMAL_SCALE_MAX', label: 'Nombre maximal de décimales', group: 'Numérique', inputMode: 'single', supportedDataTypes: ['decimal'], placeholder: '2', help: 'Limite le nombre de décimales autorisées.'},
                {code: 'INTEGER_ONLY', label: 'Entiers uniquement', group: 'Numérique', inputMode: 'none', supportedDataTypes: ['integer', 'decimal'], help: 'Refuse les valeurs décimales.'},
                {code: 'POSITIVE_ONLY', label: 'Valeurs strictement positives', group: 'Numérique', inputMode: 'none', supportedDataTypes: ['integer', 'decimal'], help: 'La valeur doit être strictement supérieure à zéro.'},
                {code: 'NEGATIVE_FORBIDDEN', label: 'Valeurs négatives interdites', group: 'Numérique', inputMode: 'none', supportedDataTypes: ['integer', 'decimal'], help: 'La valeur ne peut pas être négative.'},
                {code: 'MIN_LENGTH', label: 'Longueur minimale', group: 'Texte', inputMode: 'single', supportedDataTypes: ['text'], placeholder: '3', help: 'Le texte doit contenir au moins ce nombre de caractères.'},
                {code: 'MAX_LENGTH', label: 'Longueur maximale', group: 'Texte', inputMode: 'single', supportedDataTypes: ['text'], placeholder: '120', help: 'Le texte ne doit pas dépasser ce nombre de caractères.'},
                {code: 'REGEX', label: 'Expression régulière', group: 'Texte', inputMode: 'single', supportedDataTypes: ['text'], placeholder: '^[^<>]*$', help: 'Le texte doit respecter ce motif.'},
                {code: 'NOT_BLANK', label: 'Valeur non vide', group: 'Texte', inputMode: 'none', supportedDataTypes: ['text'], help: 'Refuse les chaînes vides ou composées uniquement d\'espaces.'},
                {code: 'FORBIDDEN_WORDS', label: 'Mots interdits', group: 'Texte', inputMode: 'list', supportedDataTypes: ['text'], placeholder: 'mot1;mot2', help: 'Refuse les textes contenant un des mots indiqués.'},
                {code: 'ALLOWED_VALUES', label: 'Valeurs texte autorisées', group: 'Texte', inputMode: 'list', supportedDataTypes: ['text'], placeholder: 'valeur1;valeur2', help: 'Limite le champ texte à cette liste fermée.'},
                {code: 'DATE_NOT_IN_FUTURE', label: 'Date non future', group: 'Date', inputMode: 'none', supportedDataTypes: ['date', 'datetime'], help: 'Refuse une date postérieure à aujourd\'hui.'},
                {code: 'DATE_NOT_BEFORE', label: 'Date minimale', group: 'Date', inputMode: 'single', supportedDataTypes: ['date', 'datetime'], placeholder: '2026-01-01', help: 'La date doit être postérieure ou égale à cette borne.'},
                {code: 'DATE_NOT_AFTER', label: 'Date maximale', group: 'Date', inputMode: 'single', supportedDataTypes: ['date', 'datetime'], placeholder: '2026-12-31', help: 'La date doit être antérieure ou égale à cette borne.'},
                {code: 'DATE_AGE_MAX_DAYS', label: 'Ancienneté maximale en jours', group: 'Date', inputMode: 'single', supportedDataTypes: ['date', 'datetime'], placeholder: '30', help: 'Déclenche si la date est trop ancienne.'},
                {code: 'DATE_AGE_MIN_DAYS', label: 'Ancienneté minimale en jours', group: 'Date', inputMode: 'single', supportedDataTypes: ['date', 'datetime'], placeholder: '1', help: 'Impose un nombre minimal de jours entre la date et aujourd\'hui.'},
                {code: 'VALUE_IN_ENUM', label: 'Valeur dans la liste', group: 'Liste de choix', inputMode: 'list', supportedDataTypes: ['list'], placeholder: 'A;B;C', help: 'La valeur sélectionnée doit appartenir à cette liste.'},
                {code: 'MULTISELECT_MIN_COUNT', label: 'Nombre minimal de choix', group: 'Liste de choix', inputMode: 'single', supportedDataTypes: ['list'], placeholder: '1', help: 'Impose un nombre minimal de choix sur un multi-sélecteur.'}
            ];
        }

        function getValidationType(code) {
            var result = null;
            angular.forEach(vm.validationTypes, function(item) {
                if (!result && item.code === code) {
                    result = item;
                }
            });
            return result || {inputMode: 'none'};
        }

        function parseValidationRules(validationsJson) {
            if (!validationsJson) {
                return [];
            }
            try {
                return angular.fromJson(validationsJson).map(function(rule) {
                    return {
                        type: rule.type,
                        severity: rule.severity || 'ERROR',
                        value: rule.value,
                        valuesText: angular.isArray(rule.values) ? rule.values.join(';') : ''
                    };
                });
            } catch (e) {
                return [];
            }
        }

        function serializeValidationRules(rules) {
            return (rules || []).filter(function(rule) {
                return !!rule.type;
            }).map(function(rule) {
                var serialized = {
                    type: rule.type,
                    severity: rule.severity || 'ERROR'
                };
                if (requiresSingleValue(rule) && rule.value !== null && rule.value !== undefined && rule.value !== '') {
                    serialized.value = rule.value;
                }
                if (requiresValuesList(rule)) {
                    serialized.values = splitInlineValues(rule.valuesText);
                }
                return serialized;
            });
        }

        function syncPossibleValuesFromRules() {
            if (!supportsPossibleValues()) {
                return;
            }
            if (splitMultilineValues(vm.valuePossibleText).length) {
                return;
            }

            var values = [];
            angular.forEach(vm.validationRules, function(rule) {
                if ((rule.type === 'VALUE_IN_ENUM' || rule.type === 'ALLOWED_VALUES') && rule.valuesText) {
                    values = splitInlineValues(rule.valuesText);
                }
            });

            if (values.length) {
                vm.valuePossibleText = joinLines(values);
            }
        }

        function parseConstraints(constraintsJson) {
            var source = parseJsonObject(constraintsJson);
            var model = {
                values: {},
                additional: []
            };

            angular.forEach(vm.constraintDefinitions, function(definition) {
                if (source.hasOwnProperty(definition.key)) {
                    model.values[definition.key] = formatConstraintValue(source[definition.key]);
                    delete source[definition.key];
                }
            });

            angular.forEach(source, function(value, key) {
                model.additional.push({
                    key: key,
                    value: formatConstraintValue(value)
                });
            });

            return model;
        }

        function serializeConstraints() {
            var serialized = {};

            angular.forEach(vm.constraintDefinitions, function(definition) {
                var rawValue = vm.constraintsModel.values[definition.key];
                if (isBlankValue(rawValue)) {
                    return;
                }
                serialized[definition.key] = normalizeConstraintValue(rawValue, definition.valueType);
            });

            angular.forEach(vm.constraintsModel.additional || [], function(item) {
                if (!item || isBlankValue(item.key) || isBlankValue(item.value)) {
                    return;
                }
                serialized[String(item.key).trim()] = parseLooseValue(item.value);
            });

            return serialized;
        }

        function parseStringArray(source) {
            if (!source) {
                return [];
            }
            try {
                var parsed = angular.fromJson(source);
                if (angular.isArray(parsed)) {
                    return parsed.map(function(item) {
                        return String(item).trim();
                    }).filter(function(item) {
                        return item.length > 0;
                    });
                }
            } catch (e) {
                return splitMultilineValues(source);
            }
            return splitMultilineValues(source);
        }

        function parseJsonObject(source) {
            if (!source) {
                return {};
            }
            try {
                var parsed = angular.fromJson(source);
                return angular.isObject(parsed) && !angular.isArray(parsed) ? parsed : {};
            } catch (e) {
                return {};
            }
        }

        function joinLines(values) {
            return (values || []).join('\n');
        }

        function splitMultilineValues(valuesText) {
            if (!valuesText) {
                return [];
            }
            return String(valuesText).split(/\r?\n|;/).map(function(item) {
                return item.trim();
            }).filter(function(item) {
                return item.length > 0;
            });
        }

        function splitInlineValues(valuesText) {
            if (!valuesText) {
                return [];
            }
            return String(valuesText).split(/[;,]/).map(function(item) {
                return item.trim();
            }).filter(function(item) {
                return item.length > 0;
            });
        }

        function formatConstraintValue(value) {
            if (value === null || value === undefined) {
                return '';
            }
            if (angular.isObject(value)) {
                return angular.toJson(value);
            }
            return String(value);
        }

        function normalizeConstraintValue(value, valueType) {
            var normalizedNumber;
            if (valueType === 'integer') {
                normalizedNumber = parseInt(value, 10);
                if (isNaN(normalizedNumber)) {
                    throw new Error('Invalid integer constraint');
                }
                return normalizedNumber;
            }
            if (valueType === 'number') {
                normalizedNumber = Number(value);
                if (isNaN(normalizedNumber)) {
                    throw new Error('Invalid numeric constraint');
                }
                return normalizedNumber;
            }
            return String(value);
        }

        function parseLooseValue(value) {
            if (value === null || value === undefined) {
                return value;
            }
            var trimmed = String(value).trim();
            if (!trimmed.length) {
                return trimmed;
            }
            if (trimmed === 'true') {
                return true;
            }
            if (trimmed === 'false') {
                return false;
            }
            if (trimmed === 'null') {
                return null;
            }
            if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
                return Number(trimmed);
            }
            if ((trimmed.charAt(0) === '{' && trimmed.charAt(trimmed.length - 1) === '}')
                    || (trimmed.charAt(0) === '[' && trimmed.charAt(trimmed.length - 1) === ']')
                    || (trimmed.charAt(0) === '"' && trimmed.charAt(trimmed.length - 1) === '"')) {
                try {
                    return angular.fromJson(trimmed);
                } catch (e) {
                    return trimmed;
                }
            }
            return trimmed;
        }

        function isBlankValue(value) {
            return value === null || value === undefined || String(value).trim().length === 0;
        }

        function defaultValidationTypeForCurrentField() {
            var dataType = effectiveDataType(vm.fieldDefinition.dataType);
            if (dataType === 'integer' || dataType === 'decimal') {
                return 'MIN_VALUE';
            }
            if (dataType === 'date' || dataType === 'datetime') {
                return 'DATE_NOT_IN_FUTURE';
            }
            if (dataType === 'list') {
                return 'VALUE_IN_ENUM';
            }
            return 'NOT_BLANK';
        }

        function isCompatibleWithCurrentType(validationType) {
            if (!validationType || !validationType.supportedDataTypes || !validationType.supportedDataTypes.length) {
                return true;
            }
            return validationType.supportedDataTypes.indexOf(effectiveDataType(vm.fieldDefinition.dataType)) !== -1;
        }

        function supportsPossibleValues() {
            return effectiveDataType(vm.fieldDefinition.dataType) === 'list';
        }

        function onDataTypeChange() {
            applyDataTypePreset(true);
        }

        function applyDataTypePreset(overrideValues) {
            if (vm.fieldDefinition.dataType === 'satisfaction_smiley') {
                if (overrideValues || !splitMultilineValues(vm.valuePossibleText).length) {
                    vm.valuePossibleText = joinLines(defaultSmileyOptions());
                }
                return;
            }
            if (overrideValues && !supportsPossibleValues()) {
                vm.valuePossibleText = '';
            }
        }

        function buildDataTypeOptions() {
            return [
                {code: 'text', label: 'Texte'},
                {code: 'integer', label: 'Entier'},
                {code: 'decimal', label: 'Décimal'},
                {code: 'boolean', label: 'Booléen'},
                {code: 'date', label: 'Date'},
                {code: 'datetime', label: 'Date/heure'},
                {code: 'list', label: 'Liste'},
                {code: 'satisfaction_smiley', label: 'Satisfaction smiley'},
                {code: 'ref_contrat', label: 'RefContrat'}
            ];
        }

        function effectiveDataType(dataType) {
            if (dataType === 'satisfaction_smiley') {
                return 'list';
            }
            if (dataType === 'ref_contrat') {
                return 'text';
            }
            return dataType;
        }

        function defaultSmileyOptions() {
            return ['very_unhappy', 'neutral', 'somewhat_happy', 'very_happy'];
        }

        function resolveValueOptionsForSave() {
            if (vm.fieldDefinition.dataType === 'satisfaction_smiley') {
                return defaultSmileyOptions();
            }
            return splitMultilineValues(vm.valuePossibleText);
        }
    }
})();
